import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#syncIhhAvailability();
    this.#ensureIhhPriceOrder();
    this.addEventListener('click', this.#handleIhhLink);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleIhhCollectionUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleIhhLink);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleIhhCollectionUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    // Pagination is a later sibling of the facets, so refresh after the whole morph.
    queueMicrotask(() => { if (this.isConnected) this.#syncIhhAvailability(); });
  }

  #priceLoadPending = false;

  #ensureIhhPriceOrder = async () => {
    const grid = this.querySelector('[ref="grid"]');
    if (this.#priceLoadPending || !grid || grid.dataset.ihhSorted || !['price-ascending', 'price-descending'].includes(this.dataset.ihhPriceOrder)) return;
    this.#priceLoadPending = true;
    grid.setAttribute('aria-busy', 'true');
    const status = document.createElement('p');
    status.className = 'ihh-price-sort-status';
    status.setAttribute('role', 'status');
    status.textContent = this.dataset.ihhPriceLoadingLabel;
    grid.before(status);
    try {
      await sectionRenderer.renderSection(this.sectionId);
      // The initial price hydration can replace only the pagination subtree.
      if (this.isConnected) this.#syncIhhAvailability();
    } catch (error) {
      if (error.name !== 'AbortError' && this.isConnected) {
        status.textContent = this.dataset.ihhPriceErrorLabel;
        status.setAttribute('role', 'alert');
      }
    } finally {
      this.#priceLoadPending = false;
      this.querySelector('[ref="grid"]')?.removeAttribute('aria-busy');
      if (this.querySelector('[data-ihh-sorted]')) status.remove();
    }
  };

  // Preserve the IHH availability preference alongside native facet updates.
  #syncIhhAvailability = () => {
    if (this.dataset.ihhCollectionKind !== 'hub') return;
    const current = new URL(window.location.href);
    const showSoldOut = current.searchParams.get('ihh_show_sold_out') === '1';
    for (const link of this.querySelectorAll('.pagination a[href]')) this.#syncIhhPageLink(link);
    const toggle = this.querySelector('.ihh-sold-out-toggle');
    if (toggle instanceof HTMLAnchorElement) {
      const next = new URL(current);
      next.searchParams.delete('page');
      next.searchParams.delete('filter.v.availability');
      next.searchParams.delete('ihh_show_sold_out');
      next.searchParams.set(showSoldOut ? 'filter.v.availability' : 'ihh_show_sold_out', '1');
      next.hash = 'ResultsList';
      toggle.href = next.toString();
      toggle.textContent = showSoldOut ? this.dataset.ihhHideSoldOut ?? '' : this.dataset.ihhShowSoldOut ?? '';
      toggle.classList.toggle('ihh-sold-out-toggle--active', showSoldOut);
    }
    for (const form of this.querySelectorAll('facets-form-component form')) {
      const previous = form.querySelector('input[data-ihh-sold-out-input]');
      if (!showSoldOut) {
        previous?.remove();
      } else if (!previous) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'ihh_show_sold_out';
        input.value = '1';
        input.dataset.ihhSoldOutInput = '';
        form.append(input);
      }
    }
  };

  /** Preserve custom stock preference and raw facet unions across native page links. */
  #syncIhhPageLink(link) {
    const original = new URL(link.href);
    const target = new URL(window.location.href);
    target.searchParams.delete('section_id');
    target.searchParams.set('page', original.searchParams.get('page') || '1');
    target.hash = original.hash;
    link.href = target.href;
  }

  /** @param {import('@shopify/events').CollectionUpdateEvent} event */
  #handleIhhCollectionUpdate = (event) => {
    event.promise?.finally(() => this.#syncIhhAvailability()).catch(() => {});
    this.#syncIhhAvailability();
  };

  /** @param {MouseEvent} event */
  #handleIhhLink = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-ihh-filter-link], .pagination a[href]');
    if (!(link instanceof HTMLAnchorElement) || this.dataset.ihhCollectionKind !== 'hub') return;
    if (link.closest('.pagination')) {
      this.#syncIhhPageLink(link);
      return;
    }
    const current = new URL(window.location.href);
    const target = new URL(link.href);
    if (current.searchParams.get('ihh_show_sold_out') === '1') {
      target.searchParams.set('ihh_show_sold_out', '1');
      target.searchParams.delete('filter.v.availability');
    } else {
      target.searchParams.set('filter.v.availability', '1');
    }
    link.href = target.toString();
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
